import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Col, Modal, Row, ModalHeader, Input, FormGroup, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import CommonErrorLabel from '../../../common/components/CommonErrorLabel';
import creditCardIcon from '../../../assets/img/icons/credit-card.svg';
import calendarIcon from '../../../assets/img/icons/calendar_month.svg';
import keyIcon from '../../../assets/img/icons/newkey.svg';
import {
  getStripeAccountClientSecret,
  updateCreditCard,
} from '../../../mywallet/MyWalletActions';
import { validateFormField } from '../../../common/helpers/validateFormField';
import { FormFieldTypes } from '../../../constants/FormFieldTypes';
import { StripeValidationErrorType } from '../../../constants/StripeValidationError';
import CommonButton from '../../../common/components/CommonButton';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';
import SettingsSectionHeader from '../../SettingsSectionHeader';
import '../../../assets/css/componentStyles/ChangeCardDetailsModal.css';

const getCardElementOptions = (isLoading) => ({
  style: {
    base: {
      color: 'black',
      fontSize: '16px',
      '::placeholder': {
        color: '#939393',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  disabled: isLoading,
});

function ChangeCardDetailsModal({ isOpen, toggle, onSuccess }) {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const isLoading = useSelector((state) => state.navbarLoader.get('isLoading'));
  const stripeValidationMessage = useSelector((state) =>
    state.register.get('stripeValidationMessage'),
  );

  const [errors, setErrors] = useState({
    cardholderError: '',
    cvcCodeError: '',
    cardNumberError: '',
    expiryError: '',
  });
  const [cardholderName, setCardholderName] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  const cardOptions = useMemo(() => getCardElementOptions(isLoading), [isLoading]);

  const handleInputChange = useCallback((name, error) => {
    setIsEdited(true);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${name}Error`]: error ? error.message : '',
    }));
  }, []);

  const validateForm = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const updatedErrors = {
          ...errors,
          cardholderError: validateFormField(cardholderName, FormFieldTypes.STRING),
        };
        setErrors(updatedErrors);

        const { cardholderError, cvcCodeError, cardNumberError, expiryError } = updatedErrors;
        if (cardholderError || cvcCodeError || cardNumberError || expiryError) {
          reject();
        }
        resolve();
      }),
    [cardholderName, errors],
  );

  const hasAnyError = useCallback(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in errors) {
      if (errors[key]) {
        return true;
      }
    }
    return false;
  }, [errors]);

  const handleSave = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    try {
      await validateForm();
      const values = { cardholder: cardholderName };
      dispatch(
        getStripeAccountClientSecret(values, stripe, elements, (stripePaymentMethodId) =>
          dispatch(
            updateCreditCard(stripePaymentMethodId, () => {
              if (typeof onSuccess === 'function') {
                onSuccess();
              }
              toggle();
            }),
          ),
        ),
      );
    } catch (e) {
      console.error(StripeValidationErrorType.VALIDATION_ERROR);
    }
  }, [stripe, elements, validateForm, cardholderName, dispatch, onSuccess, toggle]);

  const handleCancel = useCallback(() => {
    setCardholderName('');
    setErrors({
      cardholderError: '',
      cvcCodeError: '',
      cardNumberError: '',
      expiryError: '',
    });
    setIsEdited(false);
    toggle();
  }, [toggle]);

  const handleCardholderChange = useCallback((e) => {
    setCardholderName(e.target.value);
    setIsEdited(true);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="change-card-modal"
      backdropClassName="add-address-modal-backdrop"
      centered
    >
      <ModalHeader toggle={toggle} tag="div">
        <SettingsSectionHeader header="Change Card Details" variant="noBorder" />
      </ModalHeader>
      <div className="change-card-modal__body">
        <Row className="change-card-modal-top-row">
          <Col md="6">
            <FormGroup className="form-group--bordered-label">
              <Label
                className={`form-input-field-label ${
                  isLoading ? 'form-input-field-label__disabled' : ''
                }`}
              >
                Card Holder Name
              </Label>
              <Input
                name="cardholder"
                placeholder=""
                disabled={isLoading}
                value={cardholderName}
                onChange={handleCardholderChange}
                invalid={!!errors.cardholderError}
              />
              {errors.cardholderError && <CommonErrorLabel value={errors.cardholderError} />}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup className="form-group--bordered-label">
              <Label
                className={`form-input-field-label ${
                  isLoading ? 'form-input-field-label__disabled' : ''
                }`}
              >
                Credit Card Number
              </Label>
              <div
                className={`form-control card-element-wrapper position-relative ${
                  isLoading ? 'form-control--disabled' : ''
                }`}
              >
                <CardNumberElement
                  options={getCardElementOptions(isLoading)}
                  onChange={({ error }) => handleInputChange('cardNumber', error)}
                />
                <img
                  src={creditCardIcon}
                  className="input-icon input-right-icon"
                  alt="card number"
                />
              </div>
              {stripeValidationMessage && <CommonErrorLabel value={stripeValidationMessage} />}
              <CommonErrorLabel value={errors?.cardNumberError || ''} />
            </FormGroup>
          </Col>
        </Row>
        <Row className="change-card-modal-bottom-row">
          <Col md="6">
            <FormGroup className="form-group--bordered-label">
              <Label
                className={`form-input-field-label ${
                  isLoading ? 'form-input-field-label__disabled' : ''
                }`}
              >
                Expiration Date
              </Label>
              <div
                className={`form-control card-element-wrapper position-relative ${
                  isLoading ? 'form-control--disabled' : ''
                }`}
              >
                <CardExpiryElement
                  options={getCardElementOptions(isLoading)}
                  onChange={({ error }) => handleInputChange('expiry', error)}
                />
                <img
                  src={calendarIcon}
                  className="input-icon input-right-icon"
                  alt="card exp date"
                />
              </div>
              <CommonErrorLabel value={errors?.expiryError || ''} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup className="form-group--bordered-label">
              <Label
                className={`form-input-field-label ${
                  isLoading ? 'form-input-field-label__disabled' : ''
                }`}
              >
                CVC Code
              </Label>
              <div
                className={`form-control card-element-wrapper position-relative ${
                  isLoading ? 'form-control--disabled' : ''
                }`}
              >
                <CardCvcElement
                  options={cardOptions}
                  onChange={({ error }) => handleInputChange('cvcCode', error)}
                />
                <img
                  src={keyIcon}
                  className="input-icon input-right-icon"
                  alt="card cvc number"
                />
              </div>
              <CommonErrorLabel value={errors?.cvcCodeError || ''} />
            </FormGroup>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={6}>
            <CommonButton
              className="text-sm w-100"
              label="SAVE"
              disabled={isLoading || !cardholderName || hasAnyError() || !isEdited}
              variant={CommonButtonVariants.PRIMARY_RED}
              handleClick={handleSave}
            />
          </Col>
          <Col xs={6}>
            <CommonButton
              className="text-sm w-100"
              label="CANCEL"
              variant={CommonButtonVariants.OUTLINED_RED}
              handleClick={handleCancel}
              disabled={isLoading}
            />
          </Col>
        </Row>
      </div>
    </Modal>
  );
}

ChangeCardDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ChangeCardDetailsModal;
